import React, { useState } from 'react';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';

const ProductEnquiryForm = ({ isOpen, onClose, product }) => {
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    message: '',
    quantity: '1'
  });
  const [submitting, setSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customerName.trim() || !formData.customerEmail.trim() || !formData.customerPhone.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    
    try {
      await addDoc(collection(db, 'productEnquiries'), {
        // Product Details
        productId: product.id,
        productName: product.name,
        productPrice: product.price,
        productCategory: product.category,
        productCategoryName: product.categoryName || product.category,
        productImage: product.image,
        productDescription: product.description,
        
        // Customer Details
        customerName: formData.customerName.trim(),
        customerEmail: formData.customerEmail.trim(),
        customerPhone: formData.customerPhone.trim(),
        message: formData.message.trim(),
        quantity: parseInt(formData.quantity) || 1,
        
        // Metadata
        enquiryDate: new Date(),
        status: 'new',
        createdAt: new Date()
      });

      alert('Your enquiry has been submitted successfully! We will contact you soon.');
      
      // Reset form
      setFormData({
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        message: '',
        quantity: '1'
      });
      
      onClose();
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      alert('Error submitting enquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !product) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-500 to-accent-500 text-white p-6 rounded-t-2xl">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">Product Enquiry</h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl font-bold"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Product Details Section (Read-only) */}
          <div className="bg-gradient-to-br from-burlap-50 to-cream-50 rounded-xl p-6 mb-6 border border-burlap-200">
            <h3 className="text-lg font-semibold text-burlap-800 mb-4">Product Details</h3>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Product Image */}
              <div className="space-y-4">
                <div className="w-full h-64 bg-gradient-to-br from-burlap-100 to-burlap-200 rounded-lg overflow-hidden border-2 border-burlap-200">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-full h-full bg-gradient-to-br from-burlap-100 to-burlap-200 rounded-lg flex items-center justify-center hidden">
                    <span className="text-4xl text-burlap-400">🎁</span>
                  </div>
                </div>
              </div>

              {/* Product Info */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-burlap-700 mb-1">Product Name</label>
                  <div className="bg-white p-3 rounded-lg border border-burlap-200 text-burlap-800 font-medium">
                    {product.name}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-burlap-700 mb-1">Category</label>
                  <div className="bg-white p-3 rounded-lg border border-burlap-200 text-burlap-600 capitalize">
                    {product.categoryName || product.category}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-burlap-700 mb-1">Price</label>
                  <div className="bg-white p-3 rounded-lg border border-burlap-200 text-primary-600 font-bold text-lg">
                    <span className="text-base mr-1">From ₹</span>
                    {product.price}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-burlap-700 mb-1">Description</label>
                  <div className="bg-white p-3 rounded-lg border border-burlap-200 text-burlap-600 text-sm leading-relaxed max-h-24 overflow-y-auto">
                    {product.description}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Customer Information Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">
              Your Information
            </h3>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Customer Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="customerName"
                  value={formData.customerName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                  placeholder="Enter your full name"
                  required
                />
              </div>

              {/* Customer Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  name="customerPhone"
                  value={formData.customerPhone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                  placeholder="Enter your phone number"
                  required
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Customer Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="customerEmail"
                  value={formData.customerEmail}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                  placeholder="Enter your email address"
                  required
                />
              </div>

              {/* Quantity */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quantity
                </label>
                <input
                  type="number"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleInputChange}
                  min="1"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                  placeholder="Quantity needed"
                />
              </div>
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Message (Optional)
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows="4"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors resize-vertical"
                placeholder="Any specific requirements or questions..."
              />
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-8 py-3 bg-gradient-to-r from-primary-500 to-accent-500 text-white rounded-lg hover:from-primary-600 hover:to-accent-600 transition-all duration-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Submitting...' : 'Submit Enquiry'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProductEnquiryForm;
